/* One repo's skills — what a publisher ships, and the command that installs them all. */
import React from "react";
import { Globe, Star } from "lucide-react";

import { useSkills } from "@/shared/api/skills";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { PageHeader } from "@/shared/components/ui-kit";

import { formatCount } from "../catalog";
import { CopyableCommand, LoadingDots, OwnerAvatar } from "./atoms";
import { SkillCard } from "./SkillsCatalog";

export default function PublisherPage({ repo, onBack, onOpen, onOpenRepo }: {
  repo: string;
  onBack: () => void;
  onOpen: (id: string) => void;
  onOpenRepo: (repo: string) => void;
}) {
  const list = useSkills({ repo, sort: "name" });
  const skills = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const owner = skills[0]?.owner ?? { login: repo.split("/")[0], avatar_url: null };
  const stars = skills[0]?.stars;

  return (
    <>
      <PageHeader
        title={
          <Breadcrumb parent={{ label: "Skills", onClick: onBack }}>
            <span className="truncate">{repo}</span>
          </Breadcrumb>
        }
      />

      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="max-w-[880px] mx-auto px-4 md:px-6 py-6 space-y-5 fade-in-up">
          <div className="flex items-start gap-4">
            <OwnerAvatar owner={owner} size={48} rounded={12} />
            <div className="min-w-0 flex-1">
              <h1 className="text-[17px] font-semibold tracking-[-0.01em] m-0 break-words">{repo}</h1>
              <div className="flex items-center gap-1.5 mt-1 text-[12px] text-[var(--text-tertiary)] tabular-nums flex-wrap">
                <span>{total} skill{total === 1 ? "" : "s"}</span>
                {stars != null && (
                  <>
                    <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                    <span className="inline-flex items-center gap-1">
                      <Star className="w-3 h-3" strokeWidth={1.75} />{formatCount(stars)}
                    </span>
                  </>
                )}
                <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                <a
                  href={`https://github.com/${repo}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 min-w-0 hover:text-[var(--text-primary)] hover:underline"
                >
                  <Globe className="w-3 h-3 shrink-0" strokeWidth={1.75} />
                  <span className="truncate">github.com/{repo}</span>
                </a>
              </div>
            </div>
          </div>

          <CopyableCommand cmd={`npx skills add ${repo}`} />

          {list.isPending && <LoadingDots label="Loading skills" />}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {skills.map((s) => <SkillCard key={s.id} skill={s} onOpen={onOpen} onOpenRepo={onOpenRepo} />)}
          </div>
          {list.hasNextPage && (
            <div className="flex justify-center">
              <button
                onClick={() => list.fetchNextPage()}
                disabled={list.isFetchingNextPage}
                className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] disabled:opacity-40 disabled:pointer-events-none"
              >
                {list.isFetchingNextPage ? "Loading…" : "Show more"}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
